// frontend/src/features/livestock/TankShotsRow.tsx
import { useState } from 'react';
import { Center, Group, Text, UnstyledButton } from '@mantine/core';
import { IconChevronRight, IconContainer } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import classes from './TankShotsRow.module.css';

export interface TankShotsRowProps {
  tankId: string;
  count: number;
  /** The tank's cover (chosen, else the latest shot). */
  thumbUrl: string | null;
  /** The thumbnail failed to load (its signed URL may have expired); called once per URL. */
  onImageError?: () => void;
}

export function tankShotsPath(tankId: string): string {
  return `/livestock/tank-shots?tank=${encodeURIComponent(tankId)}`;
}

/**
 * Always on the Livestock page (design 6.3): the tank's cover and "Tank shots · 14",
 * or "Add your first tank shot" before there are any. All of it links to Tank Shots.
 */
export function TankShotsRow({
  tankId,
  count,
  thumbUrl,
  onImageError,
}: TankShotsRowProps): JSX.Element {
  const label = count > 0 ? `Tank shots · ${count}` : 'Add your first tank shot';
  // The URL that failed, so a refreshed one is tried and a failing one isn't retried in a loop.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const showThumb = count > 0 && !!thumbUrl && thumbUrl !== failedUrl;
  const handleError = (): void => {
    setFailedUrl(thumbUrl);
    onImageError?.();
  };
  return (
    <UnstyledButton component={Link} to={tankShotsPath(tankId)} className={classes.row}>
      <Group gap="sm" wrap="nowrap">
        {showThumb ? (
          <img src={thumbUrl ?? undefined} alt="" className={classes.thumb} onError={handleError} />
        ) : (
          <Center className={classes.thumb}>
            <IconContainer size={16} color="var(--mantine-color-teal-6)" />
          </Center>
        )}
        <Text size="sm" fw={500} className={classes.count}>
          {label}
        </Text>
        <IconChevronRight
          size={16}
          color="var(--rdh-brand-on-light)"
          style={{ flexShrink: 0, marginLeft: 'auto' }}
        />
      </Group>
    </UnstyledButton>
  );
}
